// wyvio — Animated brand mark for splash / hero.
// Uses framer-motion to progressively draw the V shapes then reveal the wordmark.
//
// Timeline (total ~1.6s) :
//   0.00s  Big lime V starts drawing        (stroke draw-in, 0.9s ease-out)
//   0.35s  Small white v starts drawing     (stroke draw-in, 0.7s ease-out)
//   0.85s  Accent dot pops in               (scale/opacity, 0.35s spring)
//   1.00s  Wordmark "wyvio" reveals         (opacity + translateY, 0.6s)
//   1.30s  "o" accent dot pops              (scale/opacity, 0.35s spring)

import { motion } from "framer-motion";
import { palette } from "@/branding/config";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  show:   (custom) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { delay: custom.delay, duration: custom.duration, ease: "easeOut" },
      opacity:    { delay: custom.delay, duration: 0.05 },
    },
  }),
};

const pop = {
  hidden: { scale: 0, opacity: 0 },
  show:   (custom) => ({
    scale: 1,
    opacity: 1,
    transition: { delay: custom.delay, type: "spring", stiffness: 320, damping: 18 },
  }),
};

const wordFade = {
  hidden: { opacity: 0, y: 8 },
  show:   { opacity: 1, y: 0, transition: { delay: 1.0, duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const AnimatedLogo = ({
  size = 220,
  primary = palette.primary,
  secondary = palette.paper,
  wordColor = palette.paper,
  showWordmark = true,
  testId = "animated-logo",
}) => {
  // Bounding box : glyphe seul 240x220 (icon-only) ou 900x220 (avec wordmark).
  const withWord = showWordmark;
  const viewBoxW = withWord ? 900 : 240;
  const height = size;
  const width = Math.round(size * (viewBoxW / 220));

  return (
    <motion.svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${viewBoxW} 220`}
      width={width}
      height={height}
      role="img"
      aria-label="wyvio"
      data-testid={testId}
      initial="hidden"
      animate="show"
      style={{ overflow: "visible" }}
    >
      {/* Grand V (lime) */}
      <motion.path
        d="M 34 34 L 100 170 L 166 34"
        fill="none"
        stroke={primary}
        strokeWidth={34}
        strokeLinecap="round"
        strokeLinejoin="round"
        variants={draw}
        custom={{ delay: 0.0, duration: 0.9 }}
      />
      {/* Petit v (blanc) */}
      <motion.path
        d="M 128 78 L 168 158 L 208 78"
        fill="none"
        stroke={secondary}
        strokeWidth={20}
        strokeLinecap="round"
        strokeLinejoin="round"
        variants={draw}
        custom={{ delay: 0.35, duration: 0.7 }}
      />
      {/* Point d'accent icône */}
      <motion.circle
        cx="168" cy="184" r="8" fill={primary}
        variants={pop} custom={{ delay: 0.85 }}
        style={{ transformOrigin: "168px 184px" }}
      />

      {/* Wordmark */}
      {withWord && (
        <motion.g variants={wordFade}>
          <text
            x="270" y="168"
            style={{
              fontFamily: `"Sora", -apple-system, BlinkMacSystemFont, sans-serif`,
              fontWeight: 600,
              fontSize: 170,
              letterSpacing: -4,
              fill: wordColor,
            }}
          >
            wyvio
          </text>
          <motion.circle
            cx="712" cy="112" r="9" fill={primary}
            variants={pop} custom={{ delay: 1.3 }}
            style={{ transformOrigin: "712px 112px" }}
          />
        </motion.g>
      )}
    </motion.svg>
  );
};

export default AnimatedLogo;
